// GL01 합배송출고 — 순수 변환. request 비의존이라 단위 테스트가 쉽다 (GL11 mapping.ts 선례).
import type { ConsolOutboundFilter, ConsolOutboundRow, ConsolOutboundRowDto, ConsolOutboundSaveRow } from './types'

/** 표 컬럼 dataIndex(UPPER_SNAKE) ↔ BE DTO 필드(camelCase). 데모 컬럼 스펙을 그대로 쓰기 위한 다리. */
export const COLUMN_TO_DTO_FIELD = {
  ROW_TYPE: 'rowType',
  SPR_ORD_NO: 'sourceOrderNumber',
  REF_NO1: 'refNo1',
  TRS_COUNTRY: 'trsCountry',
  SPR_ORD_OPT_NO: 'sourceOrderOptionNumber',
  SPR_DLV_STATUS: 'shipperDeliveryStatus',
  REF_MEMO1: 'refMemo1',
  REF_MEMO2: 'refMemo2',
  ORD_DATE: 'orderDate',
  PAY_DATE: 'payDate',
  VEN_GROUP: 'venGroup',
  SPR_ORD_STATUS: 'shipperOrderStatus',
  GDS_OPT: 'goodsOption',
  ORD_QTY: 'orderQuantity',
  REF_MEMO3: 'refMemo3',
  REF_MEMO4: 'refMemo4',
  GDS_CD: 'goodsCode',
  GDS_TYPE: 'goodsType',
  STL_NO: 'styleNo',
  COM_NM: 'companyName',
  GDS_GROUP1: 'goodsGroup1',
  IMG_URL: 'imageUrl',
  BRD_NM: 'brandName',
  GDS_LOCATION: 'goodsLocation',
  GDS_NM: 'goodsName',
  VEN_GI_TYPE: 'venGiType',
  VEN_PGI_DATE: 'venPgiDate',
  VEN_CENTERCD: 'venCenterCode',
  VEN_CENTERNM: 'venCenterName',
  VEN_DLV_TYPE: 'venDeliveryType',
  REF_CENTER1: 'refCenter1',
  REF_CENTER2: 'refCenter2',
  REF_CENTER3: 'refCenter3',
  REF_CENTER4: 'refCenter4',
  REF_CENTER5: 'refCenter5',
  REF_CENTER6: 'refCenter6',
  REF_CENTER7: 'refCenter7',
  ORD_NM: 'ordererName',
  ORD_ID: 'ordererId',
  ORD_EMAIL: 'ordererEmail',
  SPR_CUT_DATE: 'shipperCutDate',
  VEN_GI_DATE: 'venGiDate',
  REF_DATE1: 'refDate1',
  REF_DATE2: 'refDate2',
  DLV_DATE: 'deliveryDate',
  REF_DATE3: 'refDate3',
  REF_PERIOD1: 'refPeriod1',
  REF_PERIOD2: 'refPeriod2',
  REF_PERIOD3: 'refPeriod3',
  INB_TRS_INVOICE: 'inboundInvoiceNo',
  REF_URL1: 'refUrl1',
  REF_LSP1: 'refLsp1',
  REF_NO2: 'refNo2',
  REF_URL2: 'refUrl2',
  REF_LSP2: 'refLsp2',
  CSM_NM: 'consumerName',
  CSM_ZIP: 'consumerZipcode',
  CSM_STATE: 'consumerState',
  CSM_CITY: 'consumerCity',
  CSM_ADDR1: 'consumerAddress1',
  CSM_ADDR2: 'consumerAddress2',
  INB_CENTER: 'inboundCenter',
  OUB_CENTER: 'outboundCenter',
  INB_PDATE: 'inboundPlanDate',
  INB_ADATE: 'inboundActDate',
  OUB_PDATE: 'outboundPlanDate',
  OUB_ADATE: 'outboundActDate',
  ORD_CONSOL_NO: 'consolidatedNo',
  INV_TYPE: 'invoiceType',
  SVC_TYPE: 'serviceType',
  CLS_DATE: 'clsDate',
  CUT_SEQ: 'cutSeq',
  OUB_CLS_DATE: 'outboundClsDate',
  OUB_CUT_SEQ: 'outboundCutSeq',
  INB_CLS_DATE: 'inboundClsDate',
  INB_CUT_SEQ: 'inboundCutSeq',
  ORD_TYPE: 'orderType',
  TRS_TYPE: 'trsType',
  SPR_NM: 'shipperCode',
  OUB_TRS_INVOICE: 'outboundInvoiceNo',
  ORD_NO: 'orderNo',
  ORD_OPT_NO: 'orderOptionNo',
  REF_DATE4: 'refDate4',
  ORD_STATUS: 'orderStatus',
} as const satisfies Record<string, keyof ConsolOutboundRowDto>

/** 편집 가능한 컬럼과 저장 DTO 필드 — 한 매핑에서 편집 판정과 payload 생성을 함께 파생한다. */
const EDITABLE_COLUMN_TO_SAVE_FIELD = {
  REF_MEMO1: 'refMemo1',
  REF_MEMO2: 'refMemo2',
  REF_MEMO3: 'refMemo3',
  REF_MEMO4: 'refMemo4',
} as const satisfies Record<string, keyof Omit<ConsolOutboundSaveRow, 'id'>>

type EditableColumn = keyof typeof EDITABLE_COLUMN_TO_SAVE_FIELD
export const EDITABLE_COLUMNS = Object.keys(EDITABLE_COLUMN_TO_SAVE_FIELD) as EditableColumn[]
const editableValue = (value: unknown): string => (typeof value === 'string' ? value : '')

/** 셀은 문자열로 그린다 — null 은 빈칸, 숫자는 그대로 둔다(우측 정렬 컬럼 표기 유지). */
const cellValue = (v: unknown): unknown => (v === null || v === undefined ? '' : v)

/** 행 키는 PK 만으로 — 구간마다 index 가 0 부터 다시 세어져 index 를 섞으면 구간 사이 키가 겹친다(LOGISPRD-15271). */
export const toRow = (dto: ConsolOutboundRowDto): ConsolOutboundRow => {
  const row: Record<string, unknown> = { _id: dto.id, _rowKey: `r-${dto.id}` }
  Object.entries(COLUMN_TO_DTO_FIELD).forEach(([column, field]) => {
    row[column] = cellValue(dto[field as keyof ConsolOutboundRowDto])
  })
  return row as ConsolOutboundRow
}

/** 저장 payload — 실제로 바뀐 메모만 담아 다른 사용자의 동시 편집값을 덮지 않는다. */
export const toSaveRow = (row: ConsolOutboundRow, original?: ConsolOutboundRow): ConsolOutboundSaveRow => {
  const payload: ConsolOutboundSaveRow = { id: row._id }

  EDITABLE_COLUMNS.forEach((column) => {
    const field = EDITABLE_COLUMN_TO_SAVE_FIELD[column]
    const value = editableValue(row[column])
    if (!original || value !== editableValue(original[column])) payload[field] = value
  })

  return payload
}

/**
 * 표가 돌려준 행을 기존 목록에 얹는다 — 돌려받은 것으로 통째로 갈아치우지 않는다.
 *
 * 표에 넘기는 dataSource 는 목록 검색어로 거른 부분집합이다. 돌려받은 배열을 그대로 상태에 쓰면
 * 화면 밖 행이 사라진다 — 검색어를 지웠을 때 나머지가 없어지고, 저장 대상도 보이던 행만 남는다.
 * 행 키로 찾아 바뀐 것만 교체하고 나머지는 그대로 둔다.
 */
export const mergeEditedRows = (rows: ConsolOutboundRow[], edited: ConsolOutboundRow[]): ConsolOutboundRow[] => {
  const byKey = new Map(edited.map((r) => [r._rowKey, r]))
  return rows.map((r) => byKey.get(r._rowKey) ?? r)
}

/** 편집 여부 — 조회 직후 스냅샷과 현재 값을 편집 4필드에서만 비교한다. */
export const isDirtyRow = (row: ConsolOutboundRow, original?: ConsolOutboundRow): boolean =>
  !original ||
  EDITABLE_COLUMNS.some((column) => editableValue(row[column]) !== editableValue(original[column]))

/**
 * 목록 검색 — 화면에 보이는 값에만 걸린다.
 *
 * 행에는 표시하지 않는 값도 실려 있다(ROW_TYPE 은 색상 막대 계산용이라 글자로 안 보인다).
 * 검색 대상을 행 전체로 두면 안 보이는 값에 걸린 행이 남아 사용자가 이유를 알 수 없다.
 * 소비 측이 표시 컬럼 목록을 넘긴다.
 */
export const matchesQuery = (row: Record<string, unknown>, query: string, visibleKeys?: readonly string[]): boolean => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const keys = visibleKeys ?? Object.keys(row).filter((k) => !k.startsWith('_'))
  return keys.some((k) => String(row[k] ?? '').toLowerCase().includes(q))
}

/** 다운로드 사유 — 사유코드와 상세사유를 한 문자열로 합쳐 BE 감사로그에 남긴다 (GL11 선례). */
export const toDetailedReason = (reasonCode: string, reasonDetail: string): string =>
  [reasonCode, reasonDetail].filter(Boolean).join('_')

// ── 필터 폼 ↔ 조회 파라미터 ────────────────────────────────────────────────
// FilterArea 는 필드 id 를 키로 값을 준다. GL01 날짜 파라미터는 yyyyMMdd (1.0 검색폼 형식 보존).

const asList = (v: unknown): string[] | undefined => {
  if (Array.isArray(v) && v.length && typeof v[0] !== 'object') return v.map(String).filter(Boolean)
  return typeof v === 'string' && v.trim() ? [v.trim()] : undefined
}

const asYmd = (v: unknown): string | undefined => {
  if (v && typeof v === 'object' && 'format' in (v as object)) return (v as { format: (f: string) => string }).format('YYYYMMDD')
  return typeof v === 'string' && v ? v.replace(/-/g, '').slice(0, 8) : undefined
}

const asText = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

/** '전체' 옵션(allOption)은 값 미지정과 같다. */
const asSelect = (v: unknown): string | undefined => (asText(v) === '__ALL__' ? undefined : asText(v))

export const toSearchFilter = (values: Record<string, unknown>): ConsolOutboundFilter => {
  const ordDate = values.ORD_DATE as unknown[] | undefined
  const oubPdate = values.OUB_PDATE as unknown[] | undefined
  return {
    shipperCode: String(values.SPR_NM ?? ''),
    orderDateFrom: Array.isArray(ordDate) ? asYmd(ordDate[0]) : undefined,
    orderDateTo: Array.isArray(ordDate) ? asYmd(ordDate[1]) : undefined,
    outboundPlannedDateFrom: Array.isArray(oubPdate) ? asYmd(oubPdate[0]) : undefined,
    outboundPlannedDateTo: Array.isArray(oubPdate) ? asYmd(oubPdate[1]) : undefined,
    sourceOrderNumbers: asList(values.SPR_ORD_NO),
    inboundInvoiceNumber: asText(values.INB_TRS_INVOICE),
    globalInvoiceNumbers: asList(values.REF_NO2),
    orderNumbers: asList(values.ORD_NO),
    serviceType: asSelect(values.SVC_TYPE),
    orderStatus: asSelect(values.ORD_STATUS),
  }
}

/** 주문일자는 필수 필터 — 양끝이 다 있어야 조회를 보낸다. */
export const hasOrderDateRange = (filter: ConsolOutboundFilter): boolean => !!(filter.orderDateFrom && filter.orderDateTo)

/**
 * 에러 메시지 — BE meta.message 우선, 없으면 기본 문구.
 *
 * axios 에러 응답 모양을 읽으므로 엄밀히는 순수 변환이 아니다. 이 파일에 두는 건 화면 코드가
 * 여기서 가져가고 있어서다 — 옮기려면 소비처를 같은 커밋에서 함께 고쳐야 한다.
 */
export const apiErrMessage = (e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data
  return data?.meta?.message || data?.message || fallback
}
